import type { IProductivityCoverage } from '@interfaces/IProductivity';

/**
 * The prerequisites a committed activity needs before productivity can be
 * computed for it, in the order a planner would satisfy them: author a crew,
 * map it to a place, assign a trade.
 */
export type CoveragePrerequisite = 'crew' | 'location' | 'assignee';

const PREREQUISITE_ORDER: readonly CoveragePrerequisite[] = ['crew', 'location', 'assignee'];

export interface ICoverageLine {
  prerequisite: CoveragePrerequisite;
  satisfied: boolean; // Nothing is missing this one
  count: number; // Activities the line is about — those MISSING it, or those that have it
}

/**
 * One checklist line per prerequisite. A satisfied line still renders (as a ✓):
 * the checklist teaches what productivity needs, and a list of only failures
 * teaches half of it.
 *
 * `count` flips meaning with `satisfied` — an unsatisfied line counts the
 * activities that LACK the prerequisite, a satisfied one counts those that have
 * it — because each line is phrased around the number a planner would act on.
 */
export function coverageLines(coverage: IProductivityCoverage): ICoverageLine[] {
  return PREREQUISITE_ORDER.map((prerequisite) => {
    const missing = coverage.missing[prerequisite];
    return {
      prerequisite,
      satisfied: missing === 0,
      count: missing === 0 ? coverage.total : missing,
    };
  });
}

/**
 * The prerequisite the most activities are missing, or `null` when the plan is
 * fully authored. Drives the one-line partial-coverage bar, which has room to
 * name a single gap and should name the biggest.
 */
export function largestShortfall(coverage: IProductivityCoverage): ICoverageLine | null {
  const shortfalls = coverageLines(coverage).filter((line) => !line.satisfied);
  if (shortfalls.length === 0) return null;
  return shortfalls.reduce((worst, line) => (line.count > worst.count ? line : worst));
}

/**
 * The site HAS a plan and none of it is measurable — the state the setup
 * checklist speaks to.
 *
 * `total > 0` is load-bearing: a site with no schedule imported also reports
 * `measured === 0`, but it has nothing to set up, and a checklist reading "0 of
 * 0 activities" would be nonsense. That case falls through to the ordinary page.
 */
export function isUnconfigured(coverage: IProductivityCoverage): boolean {
  return coverage.total > 0 && coverage.measured === 0;
}

/** Some, but not all, of the plan is measurable — every figure on the page is a
 *  partial view, and the surfaces say so rather than implying full coverage. */
export function isPartiallyConfigured(coverage: IProductivityCoverage): boolean {
  return coverage.measured > 0 && coverage.measured < coverage.total;
}

/**
 * Any of the plan is unmeasurable — the condition for saying anything about
 * coverage at all. Covers both incomplete states; `isUnconfigured` distinguishes
 * the severe one, which only decides how much of the story shows by default.
 *
 * False for a site with no schedule (`total === 0`): nothing to measure is not
 * the same as failing to measure something.
 */
export function hasCoverageGap(coverage: IProductivityCoverage): boolean {
  return coverage.total > 0 && coverage.measured < coverage.total;
}